import { SearchJobsInterface } from 'constants/interfaces';
import { SearchJobsInitialValues, searchJobsSchema } from 'constants/schemaValidations';
import { useFormik } from 'formik';
import React, { ReactNode } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useIndustriesListQuery } from 'services';
import ReactSelect from 'react-select';
import useTranslate from 'hooks/useTranslate';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './styles.scss';

const SearchJobs: React.FC = () => {
	const { translate } = useTranslate();
	const { data: { industries: industrieslist = [] } = {} } = useIndustriesListQuery();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const paramsObject = Object.fromEntries(searchParams.entries());

	const {
		keyword = '',
		location = '',
		industryId = '',
		cityId = '',
		stateId = '',
		countryId = '',
	} = paramsObject;

	const formik: any = useFormik({
		initialValues: {
			...SearchJobsInitialValues,
			keyword: keyword,
			cityId: cityId ? [cityId] : [],
			stateId: stateId ? [stateId] : [],
			countryId: countryId ? [countryId] : [],
			location: location ? [decodeURIComponent(location)] : [],
			industryId: industryId,
		},
		enableReinitialize: true,
		validationSchema: searchJobsSchema(translate),
		onSubmit: (values: any) => {
			const keyToConstantMap: any = {
				keyword: values?.keyword || '',
				cityId: values?.cityId[0] || '',
				stateId: values?.stateId[0] || '',
				countryId: values?.countryId[0] || '',
				location: values?.location[0] || '',

				industryId: values?.industryId || '',
			};

			const paramsObjectIsEmpty = Object.keys(paramsObject).length === 0;

			const queryString = Object.keys(paramsObjectIsEmpty ? keyToConstantMap : paramsObject)
				.map((key) => {
					const value = keyToConstantMap[key] ?? paramsObject[key] ?? '';
					return `${key}=${encodeURIComponent(value)}`;
				})
				.join('&');
			navigate(`/job-search?${queryString}`);
		},
	});

	const renderError = <T extends keyof SearchJobsInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Form className="d-flex flex-column flex-xl-row gap-2 gap-md-3" onSubmit={formik.handleSubmit}>
			<Form.Group className="search-box position-relative flex-1">
				<i className="ri-search-line filter-form-icon position-absolute"></i>
				<Form.Control
					className="rounded-pill"
					type="search"
					placeholder={translate('PlaceHolder.searchByKeyword')}
					name="keyword"
					autoComplete="off"
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					value={formik.values.keyword}
				/>
				{renderError('keyword')}
			</Form.Group>
			<Form.Group className="address-box position-relative">
				<CustomSearchAddress
					selectProps={{
						placeholder: translate('PlaceHolder.locationText'),
						onChange: (value: any) => {
							formik.setFieldValue('cityId', value.cityId ? [value.cityId] : '');
							formik.setFieldValue('stateId', value.stateId ? [value.stateId] : '');
							formik.setFieldValue('countryId', value.countryId ? [value.countryId] : '');
							formik.setFieldValue('location', value.location ? [value.location] : '');
						},
						name: 'location',
					}}
					fieldValue={formik.values.location?.[0]}
					onBlur={formik.handleBlur}
				/>
				{renderError('location')}
				<i className="ri-map-pin-line filter-form-icon position-absolute"></i>
			</Form.Group>
			<Form.Group className="sector-box position-relative">
				<ReactSelect
					isSearchable
					isClearable
					name="industryId"
					options={industrieslist}
					getOptionValue={(option) => option.id}
					getOptionLabel={(option) => option.title}
					onChange={(optionValue: any) => formik.setFieldValue('industryId', optionValue?.id)}
					onBlur={formik.handleBlur}
					autoFocus={false}
					placeholder={translate('PlaceHolder.selectIndustry')}
					className="form-react-select "
					classNamePrefix="select"
					value={industrieslist?.find((item: any) => item.id === Number(formik.values.industryId))}
				/>
				{renderError('industryId')}
				<i className="ri-function-line filter-form-icon position-absolute"></i>
			</Form.Group>
			<Button className="filled-btn rounded-pill" type="submit">
				{translate('Button.search')}
			</Button>
		</Form>
	);
};

export default SearchJobs;
